import { useState, useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Zap, Sun, Euro, Battery, Info } from 'lucide-react';
import { getDemoCurve, getDemoTodayTotals } from './demoEnergyData';

const PERIODS = ['Nu', 'Dag', 'Week', 'Maand', 'Jaar'];

function MetricTile({ label, value, unit, hint, Icon, tone = 'emerald', testid }) {
  const toneMap = {
    emerald: 'bg-emerald-50 text-emerald-900 ring-emerald-100',
    amber: 'bg-amber-50 text-amber-900 ring-amber-100',
    sky: 'bg-sky-50 text-sky-900 ring-sky-100',
    slate: 'bg-slate-50 text-slate-900 ring-slate-100',
  }[tone];
  return (
    <div
      className={`rounded-2xl ring-1 ${toneMap} p-4 sm:p-5`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs sm:text-sm font-medium opacity-80">{label}</span>
        <Icon className="w-4 h-4 sm:w-5 sm:h-5 opacity-70" />
      </div>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="text-2xl sm:text-3xl font-semibold tracking-tight">
          {value}
        </span>
        <span className="text-xs sm:text-sm opacity-70">{unit}</span>
      </div>
      {hint && <p className="text-[11px] sm:text-xs opacity-70 mt-0.5">{hint}</p>}
    </div>
  );
}

export default function EnergyDashboard({ compact = false }) {
  const [period, setPeriod] = useState('Dag');
  const data = useMemo(() => getDemoCurve(period), [period]);
  const totals = useMemo(() => getDemoTodayTotals(), []);

  return (
    <div className="space-y-4" data-testid="energy-dashboard">
      {/* Demo banner */}
      <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 text-[12px] text-blue-900"
        data-testid="energy-demo-banner">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>
          <strong>Voorbeeldweergave</strong> — koppel uw P1-meter voor echte live data.
        </span>
      </div>

      {/* 4 metric tiles */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4" data-testid="energy-metric-tiles">
        <MetricTile
          label="Verbruik vandaag"
          value={totals.verbruik_kwh}
          unit="kWh"
          hint="Stroom uit het net"
          Icon={Zap}
          tone="amber"
          testid="metric-verbruik"
        />
        <MetricTile
          label="Teruglevering vandaag"
          value={totals.teruglevering_kwh}
          unit="kWh"
          hint="Naar het net terug"
          Icon={Battery}
          tone="emerald"
          testid="metric-teruglevering"
        />
        <MetricTile
          label="Eigen zonnestroom"
          value={totals.eigen_zon_kwh}
          unit="kWh"
          hint="Zelf gebruikt"
          Icon={Sun}
          tone="sky"
          testid="metric-eigen-zon"
        />
        <MetricTile
          label="Kosten indicatie"
          value={`€\u00A0${totals.kosten_euro.toFixed(2).replace('.', ',')}`}
          unit=""
          hint="Illustratief, geen claim"
          Icon={Euro}
          tone="slate"
          testid="metric-kosten"
        />
      </div>

      {/* Chart card */}
      <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-4 sm:p-5"
        data-testid="energy-chart-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <h3 className="text-base sm:text-lg font-semibold tracking-tight">Stroom vandaag</h3>
          <div className="flex items-center gap-1 bg-slate-100 rounded-full p-0.5"
            data-testid="energy-chart-period-tabs">
            {PERIODS.map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                data-testid={`energy-chart-period-${p.toLowerCase()}`}
                className={`px-2.5 sm:px-3 py-1 text-xs sm:text-sm rounded-full transition-colors ${period === p
                  ? 'bg-white text-emerald-700 shadow-sm font-medium'
                  : 'text-slate-600 hover:text-slate-900'}`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div className={compact ? 'h-44' : 'h-52 sm:h-64'} data-testid="energy-chart-area">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="gradVerbruik" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradTeruglevering" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} width={38} />
              <Tooltip
                contentStyle={{ border: '1px solid #e2e8f0', borderRadius: 12, fontSize: 12 }}
                formatter={(v, k) => [`${v} kWh`, k === 'verbruik' ? 'Verbruik' : 'Teruglevering']}
              />
              <Legend
                wrapperStyle={{ fontSize: 11, paddingTop: 6 }}
                iconType="circle"
                formatter={(v) => v === 'verbruik' ? 'Verbruik' : 'Teruglevering'}
              />
              <Area
                type="monotone"
                dataKey="verbruik"
                stroke="#f59e0b"
                strokeWidth={2}
                fill="url(#gradVerbruik)"
              />
              <Area
                type="monotone"
                dataKey="teruglevering"
                stroke="#10b981"
                strokeWidth={2}
                fill="url(#gradTeruglevering)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[11px] text-slate-500 mt-1">
          Voorbeeldgrafiek. Echte data verschijnt zodra uw P1-meter gekoppeld is.
        </p>
      </div>
    </div>
  );
}
